/**
 * Real games that exemplify each Game-Concept facet value — shown in hover
 * tooltips on the Design Challenge and Game-Jam pickers. Keyed by axis key then
 * the exact value string. Values not listed simply show no tooltip.
 */

export const FACET_EXAMPLES: Record<string, Record<string, string[]>> = {
  genre: {
    Action: ["Devil May Cry 5", "God of War", "Bayonetta"],
    Adventure: ["The Legend of Zelda", "Uncharted 4", "Tomb Raider"],
    Platformer: ["Super Mario Bros.", "Celeste", "Rayman Legends"],
    RPG: ["Skyrim", "Baldur's Gate 3", "Final Fantasy"],
    "Action-RPG": ["The Witcher 3", "Elden Ring", "Diablo IV"],
    JRPG: ["Final Fantasy VII", "Persona 5", "Dragon Quest XI"],
    Shooter: ["Doom", "Halo", "Call of Duty"],
    "First-Person Shooter": ["Halo", "Doom (2016)", "Half-Life 2"],
    "Third-Person Shooter": ["Gears of War", "Max Payne 3", "The Division"],
    Fighting: ["Street Fighter 6", "Tekken 8", "Super Smash Bros."],
    Racing: ["Mario Kart 8", "Forza Horizon 5", "Gran Turismo 7"],
    Sports: ["EA Sports FC", "NBA 2K", "Rocket League"],
    Simulation: ["The Sims 4", "Microsoft Flight Simulator", "Kerbal Space Program"],
    Strategy: ["Civilization VI", "StarCraft II", "Age of Empires IV"],
    "Real-Time Strategy": ["StarCraft II", "Age of Empires IV", "Command & Conquer"],
    "Turn-Based Strategy": ["Civilization VI", "XCOM 2", "Into the Breach"],
    Puzzle: ["Tetris", "The Witness", "Portal"],
    Survival: ["Minecraft", "Rust", "The Forest"],
    Horror: ["Resident Evil", "Silent Hill 2", "Amnesia"],
    Stealth: ["Metal Gear Solid V", "Dishonored", "Hitman"],
    Sandbox: ["Minecraft", "Terraria", "Garry's Mod"],
    Roguelike: ["Hades", "The Binding of Isaac", "Dead Cells"],
    Metroidvania: ["Hollow Knight", "Metroid Dread", "Castlevania: SotN"],
    "Visual Novel": ["Ace Attorney", "Doki Doki Literature Club", "Steins;Gate"],
    Rhythm: ["Guitar Hero", "Beat Saber", "Crypt of the NecroDancer"],
    "Card Game": ["Hearthstone", "Slay the Spire", "Magic: The Gathering Arena"],
    MOBA: ["League of Legends", "Dota 2", "Heroes of the Storm"],
    MMORPG: ["World of Warcraft", "Final Fantasy XIV", "Guild Wars 2"],
    "Battle Royale": ["Fortnite", "Apex Legends", "PUBG"],
    "Tower Defense": ["Bloons TD 6", "Plants vs. Zombies", "Kingdom Rush"],
    "Point-and-Click": ["Monkey Island", "Grim Fandango", "Machinarium"],
    "Idle / Incremental": ["Cookie Clicker", "AdVenture Capitalist", "Universal Paperclips"],
    "Party Game": ["Mario Party", "Jackbox", "Fall Guys"],
    "Tycoon / Management": ["RollerCoaster Tycoon", "Planet Coaster", "Two Point Hospital"],
    "Open World": ["Grand Theft Auto V", "Breath of the Wild", "Red Dead Redemption 2"],
  },
  subgenre: {
    Soulslike: ["Dark Souls", "Elden Ring", "Lies of P"],
    "Looter-Shooter": ["Destiny 2", "Borderlands 3", "The Division 2"],
    "Hero Shooter": ["Overwatch 2", "Valorant", "Team Fortress 2"],
    "Extraction Shooter": ["Escape from Tarkov", "Hunt: Showdown", "The Cycle"],
    "Boomer Shooter": ["DUSK", "ULTRAKILL", "Amid Evil"],
    "Survival Horror": ["Resident Evil 2", "Silent Hill 2", "Alien: Isolation"],
    "Psychological Horror": ["Amnesia", "SOMA", "Layers of Fear"],
    "Immersive Sim": ["Deus Ex", "Dishonored", "Prey (2017)"],
    "Walking Simulator": ["Gone Home", "Firewatch", "Dear Esther"],
    "City Builder": ["Cities: Skylines", "Frostpunk", "SimCity"],
    "Colony Sim": ["RimWorld", "Dwarf Fortress", "Oxygen Not Included"],
    "Life Sim": ["The Sims", "Animal Crossing", "Stardew Valley"],
    "Farming Sim": ["Stardew Valley", "Story of Seasons", "Farming Simulator"],
    "Dating Sim": ["Dream Daddy", "Doki Doki Literature Club", "HuniePop"],
    Deckbuilder: ["Slay the Spire", "Inscryption", "Monster Train"],
    "Auto-Battler": ["Teamfight Tactics", "Hearthstone Battlegrounds", "Dota Underlords"],
    "Bullet Hell": ["Touhou", "Enter the Gungeon", "Ikaruga"],
    "Twin-Stick Shooter": ["Nuclear Throne", "Enter the Gungeon", "Geometry Wars"],
    "Hack-and-Slash": ["Devil May Cry", "Bayonetta", "God of War"],
    "Beat 'em Up": ["Streets of Rage 4", "Final Fight", "River City Girls"],
    "Run-and-Gun": ["Contra", "Cuphead", "Metal Slug"],
    "Puzzle-Platformer": ["Braid", "Limbo", "Fez"],
    "Precision Platformer": ["Celeste", "Super Meat Boy", "N++"],
    "4X": ["Civilization VI", "Stellaris", "Endless Space 2"],
    "Grand Strategy": ["Crusader Kings III", "Europa Universalis IV", "Hearts of Iron IV"],
    "Tactical RPG": ["Fire Emblem", "Final Fantasy Tactics", "XCOM 2"],
    "Dungeon Crawler": ["Diablo", "Etrian Odyssey", "Legend of Grimrock"],
    "Action Roguelike": ["Hades", "Dead Cells", "Returnal"],
    "Survival Crafting": ["Valheim", "Subnautica", "Don't Starve"],
    "Social Deduction": ["Among Us", "Town of Salem", "Goose Goose Duck"],
    "Asymmetric PvP": ["Dead by Daylight", "Evolve", "Deceit"],
    Sokoban: ["Sokoban", "Baba Is You", "Patrick's Parabox"],
    "Match-3": ["Candy Crush Saga", "Bejeweled", "Puzzle & Dragons"],
    "Endless Runner": ["Temple Run", "Subway Surfers", "Canabalt"],
    "Rhythm-Action": ["Crypt of the NecroDancer", "Hi-Fi Rush", "Metal: Hellsinger"],
    "Open-World Sandbox": ["Minecraft", "Grand Theft Auto V", "Breath of the Wild"],
  },
  mechanic: {
    "Double-Jump": ["Celeste", "Hollow Knight", "Rayman Legends"],
    "Wall-Run": ["Titanfall 2", "Ghostrunner", "Prince of Persia"],
    "Grappling Hook": ["Just Cause", "Titanfall 2", "Sekiro"],
    "Time Rewind": ["Braid", "Prince of Persia: Sands of Time", "Life is Strange"],
    "Bullet-Time": ["Max Payne", "SUPERHOT", "F.E.A.R."],
    "Gravity Flip": ["VVVVVV", "Gravity Rush", "Super Mario Galaxy"],
    "Invisibility / Cloak": ["Crysis", "Predator: Hunting Grounds", "Team Fortress 2"],
    "Possession / Body-Swap": ["Dishonored", "Geist", "Murdered: Soul Suspect"],
    "Shape-Shifting": ["Prototype", "Kirby", "Majora's Mask"],
    Portals: ["Portal", "Portal 2", "Prey (2006)"],
    "Teleport-Dash": ["Dishonored (Blink)", "Overwatch (Tracer)", "Ghostrunner"],
    "Parry / Riposte": ["Sekiro", "Dark Souls", "For Honor"],
    Crafting: ["Minecraft", "The Witcher 3", "Terraria"],
    "Base-Building": ["Fortnite", "Rust", "Valheim"],
    "Deck-Building": ["Slay the Spire", "Inscryption", "Hearthstone"],
    "Dialogue Trees": ["Mass Effect", "Disco Elysium", "The Witcher 3"],
    "Stealth Takedowns": ["Assassin's Creed", "Batman: Arkham", "Metal Gear Solid V"],
    "Hacking Mini-Game": ["Watch Dogs", "BioShock", "Deus Ex"],
    "Companion AI": ["The Last of Us", "BioShock Infinite", "Ico"],
    "Day/Night Cycle": ["Minecraft", "Majora's Mask", "Dying Light"],
    "Weather Manipulation": ["Okami", "From Dust", "Flower"],
    Summoning: ["Final Fantasy", "Elden Ring (Spirit Ashes)", "Pokémon"],
    "Weapon Crafting": ["Dead Rising", "Dying Light", "Tears of the Kingdom (Fuse)"],
    "Elemental Combos": ["Genshin Impact", "Divinity: Original Sin 2", "Magicka"],
    "Momentum-Based Movement": ["Sonic the Hedgehog", "Titanfall 2", "Tribes: Ascend"],
    "Hookshot Swing": ["Marvel's Spider-Man", "Bionic Commando", "A Hat in Time"],
    "Cover System": ["Gears of War", "The Division", "Mass Effect"],
    "Bullet Reflection": ["Star Wars Jedi: Survivor", "Metal Gear Rising", "Furi"],
    "Slow-Mo Aim": ["Red Dead Redemption 2 (Dead Eye)", "Max Payne 3", "SUPERHOT"],
    "Resource Drain": ["Don't Starve", "Frostpunk", "This War of Mine"],
    "Possessed Terrain": ["Control", "Antichamber", "Manifold Garden"],
    "Tower Placement": ["Bloons TD 6", "Kingdom Rush", "Plants vs. Zombies"],
    "Tile Matching": ["Candy Crush Saga", "Bejeweled", "Puyo Puyo"],
    "Card Drafting": ["Slay the Spire", "Dominion", "7 Wonders"],
    "Dice Rolling": ["Dicey Dungeons", "Slice & Dice", "Cultist Simulator"],
    Permadeath: ["XCOM 2", "FTL: Faster Than Light", "The Binding of Isaac"],
    "Roguelike Runs": ["Hades", "Dead Cells", "Rogue Legacy"],
    "Tether / Linked Bodies": ["Brothers: A Tale of Two Sons", "It Takes Two", "ibb & obb"],
    "Sanity Meter": ["Amnesia", "Eternal Darkness", "Don't Starve"],
    "Heat / Wanted Level": ["Grand Theft Auto V", "Need for Speed", "Watch Dogs"],
    "Morph Ball / Tight Spaces": ["Metroid Dread", "Ori and the Will of the Wisps", "Aria of Sorrow"],
    "Echolocation / Sonar": ["Perception", "Dark Echo", "Blind"],
    "Photo Mode as Tool": ["Pokémon Snap", "Beyond Good & Evil", "Umurangi Generation"],
    "Rhythm-Locked Actions": ["Crypt of the NecroDancer", "Hi-Fi Rush", "BPM: Bullets Per Minute"],
    "Sound-Based Stealth": ["Alien: Isolation", "The Last of Us", "Splinter Cell"],
    "Limited Inventory (Tetris)": ["Resident Evil 4", "Escape from Tarkov", "Deus Ex"],
    "Time Loop (Groundhog Day)": ["Outer Wilds", "Deathloop", "The Sexy Brutale"],
    "Diegetic Map (no UI)": ["Dead Space", "Metro Exodus", "Far Cry 2"],
    "One-Hit Death": ["Hotline Miami", "Katana ZERO", "Super Meat Boy"],
    "Co-op Tethered Lives": ["It Takes Two", "Overcooked", "Lovers in a Dangerous Spacetime"],
  },
  theme: {
    "High Fantasy": ["Skyrim", "Dragon Age", "Baldur's Gate 3"],
    "Dark Fantasy": ["Dark Souls", "Elden Ring", "Diablo"],
    "Sci-Fi": ["Mass Effect", "Halo", "Dead Space"],
    Cyberpunk: ["Cyberpunk 2077", "Deus Ex", "Ghostrunner"],
    Steampunk: ["BioShock Infinite", "Dishonored", "Frostpunk"],
    "Post-Apocalyptic": ["Fallout", "The Last of Us", "Metro Exodus"],
    Horror: ["Resident Evil", "Silent Hill", "Dead Space"],
    "Cosmic Horror": ["Bloodborne", "Call of Cthulhu", "Sunless Sea"],
    Noir: ["L.A. Noire", "Max Payne", "Disco Elysium"],
    Western: ["Red Dead Redemption 2", "Call of Juarez", "Desperados III"],
    "Mythology (Greek)": ["God of War", "Hades", "Assassin's Creed Odyssey"],
    "Mythology (Norse)": ["God of War (2018)", "Assassin's Creed Valhalla", "Jotun"],
    "Feudal Japan": ["Ghost of Tsushima", "Sekiro", "Nioh"],
    "Pirates / High Seas": ["Sea of Thieves", "AC IV: Black Flag", "Sid Meier's Pirates!"],
    "Space Opera": ["Mass Effect", "Star Wars: KOTOR", "Starfield"],
    Solarpunk: ["Terra Nil", "Eco", "Cloud Gardens"],
    Dieselpunk: ["BioShock", "Wolfenstein: The New Order", "Iron Harvest"],
    Medieval: ["Kingdom Come: Deliverance", "Mount & Blade", "Crusader Kings III"],
    "Modern Military": ["Call of Duty: Modern Warfare", "Battlefield", "ARMA 3"],
    Prehistoric: ["Far Cry Primal", "ARK: Survival Evolved", "Ancestors"],
    Underwater: ["Subnautica", "BioShock", "Abzû"],
    "Heist / Crime": ["Payday 2", "Grand Theft Auto V", "Monaco"],
    Supernatural: ["Control", "Alan Wake", "Phasmophobia"],
    Fairytale: ["Child of Light", "The Wolf Among Us", "American McGee's Alice"],
    "Dystopian Corporate": ["The Outer Worlds", "Cyberpunk 2077", "Beholder"],
    "Wild West Weird": ["Weird West", "Hard West", "West of Loathing"],
    Afrofuturism: ["Aurion: Legacy of the Kori-Odan"],
  },
};

export function facetExamples(key: string, value: string): string[] {
  return FACET_EXAMPLES[key]?.[value] ?? [];
}
